import { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Save, Trash2, Truck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/Dialog';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import DecimalInput from '@/Components/forms/DecimalInput';
import romaneioService, { type RomaneioInput, type RomaneioItem } from '@/services/romaneio';
import veiculoService, { type Veiculo } from '@/services/veiculo';
import responsavelService, { type Responsavel } from '@/services/responsavel';
import produtoService, { type Produto } from '@/services/produto';
import loteService, { type Lote } from '@/services/lote';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const ITEM_VAZIO: RomaneioItem = { produto_id: '', lote_id: '', quantidade: 0, observacoes: '' };

const FORM_VAZIO: RomaneioInput = {
  numero: '',
  responsavel_emissao_id: '',
  destinatario_nome: '',
  destinatario_cnpj: '',
  destinatario_endereco: '',
  transportadora: '',
  motorista: '',
  veiculo_id: '',
  observacoes: '',
  itens: [],
};

type ApiValidationResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

function validationLabel(field: string) {
  const itemMatch = field.match(/^itens\.(\d+)\.(.+)$/);
  if (itemMatch) {
    const row = Number(itemMatch[1]) + 1;
    const labels: Record<string, string> = {
      produto_id: 'produto',
      lote_id: 'lote',
      quantidade: 'quantidade',
      ordem_producao_id: 'ordem de produção',
    };
    return `Item ${row} - ${labels[itemMatch[2]] || itemMatch[2]}`;
  }

  const labels: Record<string, string> = {
    responsavel_emissao_id: 'Responsável emissão',
    veiculo_id: 'Veículo',
    destinatario_nome: 'Destinatário',
    destinatario_endereco: 'Endereço',
  };

  return labels[field] || field;
}

function extractErrorMessage(error: unknown) {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiValidationResponse | undefined;
    if (data?.errors) {
      return Object.entries(data.errors)
        .flatMap(([field, messages]) => messages.map((message) => `${validationLabel(field)}: ${message}`))
        .join('\n');
    }
    if (data?.message) return data.message;
  }

  return 'Não foi possível salvar o romaneio. Verifique os dados e tente novamente.';
}

export default function RomaneioForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const visualizando = !!id;
  const [form, setForm] = useState<RomaneioInput>(() => ({ ...FORM_VAZIO, numero: `ROM-${Date.now().toString().slice(-8)}` }));
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [lotesPorProduto, setLotesPorProduto] = useState<Record<number, Lote[]>>({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [modalProdutosAberto, setModalProdutosAberto] = useState(false);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([]);
  const veiculosValidos = veiculos.filter((veiculo) => getUuidCandidate(veiculo));
  const responsaveisValidos = responsaveis.filter((responsavel) => getUuidCandidate(responsavel));
  const produtosValidos = produtos.filter((produto) => getUuidCandidate(produto));
  const produtosDisponiveis = produtosValidos.filter((produto) => {
    const termo = buscaProduto.trim().toLowerCase();
    const uuid = getUuidCandidate(produto) || '';
    if (form.itens.some((item) => item.produto_id === uuid)) return false;
    if (!termo) return true;
    return `${produto.codigo || ''} ${produto.descricao || ''}`.toLowerCase().includes(termo);
  });
  const produtoPorId = (produtoId?: string | null) => produtos.find((produto) => getUuidCandidate(produto) === produtoId);

  useEffect(() => {
    veiculoService.listar().then(setVeiculos).catch(console.error);
    responsavelService.listar().then(setResponsaveis).catch(console.error);
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  useEffect(() => {
    if (!id) return;
    romaneioService.buscarPorId(id)
      .then((data) => {
        setForm({
          numero: data.numero,
          responsavel_emissao_id: data.responsavel_emissao_id || '',
          destinatario_nome: data.destinatario_nome || '',
          destinatario_cnpj: data.destinatario_cnpj || '',
          destinatario_endereco: data.destinatario_endereco || '',
          transportadora: data.transportadora || '',
          motorista: data.motorista || '',
          veiculo_id: data.veiculo_id || '',
          observacoes: data.observacoes || '',
          itens: data.itens || [],
        });
      })
      .catch((error) => {
        console.error('Erro ao carregar romaneio', error);
        setErro('Não foi possível carregar o romaneio.');
      });
  }, [id]);

  const abrirProdutos = () => {
    setBuscaProduto('');
    setProdutosSelecionados([]);
    setModalProdutosAberto(true);
  };

  const removerItem = (index: number) => {
    setForm({ ...form, itens: form.itens.filter((_, itemIndex) => itemIndex !== index) });
  };

  const atualizarItem = (index: number, patch: Partial<RomaneioItem>) => {
    const itens = [...form.itens];
    itens[index] = { ...itens[index], ...patch };
    setForm({ ...form, itens });
  };

  const carregarLotesProduto = async (index: number, produtoId: string) => {
    try {
      const lotes = await loteService.listar({ produto: produtoId, status: 'LIVRE' });
      setLotesPorProduto((current) => ({ ...current, [index]: lotes }));
    } catch (error) {
      console.error('Erro ao carregar lotes', error);
    }
  };

  const toggleProdutoSelecionado = (produtoId: string) => {
    setProdutosSelecionados((selecionados) =>
      selecionados.includes(produtoId)
        ? selecionados.filter((item) => item !== produtoId)
        : [...selecionados, produtoId],
    );
  };

  const confirmarProdutos = () => {
    if (produtosSelecionados.length === 0) return;
    const indiceInicial = form.itens.length;
    const novosItens = produtosSelecionados.map((produtoId) => ({ ...ITEM_VAZIO, produto_id: produtoId }));

    setForm({ ...form, itens: [...form.itens, ...novosItens] });
    produtosSelecionados.forEach((produtoId, index) => {
      void carregarLotesProduto(indiceInicial + index, produtoId);
    });
    setModalProdutosAberto(false);
    setProdutosSelecionados([]);
    setBuscaProduto('');
  };

  const salvar = async () => {
    setErro(null);
    if (!form.numero || !isUuid(form.responsavel_emissao_id) || !form.destinatario_nome || !form.destinatario_endereco) {
      setErro('Preencha os campos obrigatórios do romaneio.');
      return;
    }
    if (form.itens.length === 0 || form.itens.some((item) => !isUuid(item.produto_id) || Number(item.quantidade) <= 0)) {
      setErro('Adicione pelo menos um item com produto e quantidade válida.');
      return;
    }
    setSalvando(true);
    try {
      await romaneioService.criar(form);
      navigate('/producao/romaneios');
    } catch (error) {
      console.error('Erro ao salvar romaneio', error);
      setErro(extractErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<Truck size={21} />} title={visualizando ? 'Visualizar Romaneio' : 'Novo Romaneio'} description={visualizando ? 'Consulte os dados do romaneio e seus itens.' : 'Preencha os dados para emitir um novo romaneio de expedição.'} actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/romaneios')}><ArrowLeft size={16} /> Voltar</Button>} />
      {erro ? <div className="nx-form-alert nx-form-alert--error" role="alert" style={{ whiteSpace: 'pre-line' }}>{erro}</div> : null}
      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados do romaneio</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Número *" htmlFor="romaneio-numero"><Input id="romaneio-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} readOnly disabled required /></Field>
            <Field label="Responsável emissão *" htmlFor="romaneio-responsavel"><Select id="romaneio-responsavel" value={form.responsavel_emissao_id || ''} onChange={(e) => setForm({ ...form, responsavel_emissao_id: e.target.value })} disabled={visualizando} required><option value="">Selecione</option>{responsaveisValidos.map((item) => <option key={item.id} value={getUuidCandidate(item) || ''}>{item.nome}</option>)}</Select></Field>
            <Field label="Destinatário *" htmlFor="romaneio-destinatario" className="nx-sngpc-field--full"><Input id="romaneio-destinatario" value={form.destinatario_nome || ''} onChange={(e) => setForm({ ...form, destinatario_nome: e.target.value })} disabled={visualizando} required /></Field>
            <Field label="CNPJ" htmlFor="romaneio-cnpj"><Input id="romaneio-cnpj" value={form.destinatario_cnpj || ''} onChange={(e) => setForm({ ...form, destinatario_cnpj: e.target.value })} disabled={visualizando} /></Field>
            <Field label="Endereço *" htmlFor="romaneio-endereco" className="nx-sngpc-field--full"><Textarea id="romaneio-endereco" value={form.destinatario_endereco || ''} onChange={(e) => setForm({ ...form, destinatario_endereco: e.target.value })} rows={2} disabled={visualizando} required /></Field>
            <Field label="Transportadora" htmlFor="romaneio-transportadora"><Input id="romaneio-transportadora" value={form.transportadora || ''} onChange={(e) => setForm({ ...form, transportadora: e.target.value })} disabled={visualizando} /></Field>
            <Field label="Motorista" htmlFor="romaneio-motorista"><Input id="romaneio-motorista" value={form.motorista || ''} onChange={(e) => setForm({ ...form, motorista: e.target.value })} disabled={visualizando} /></Field>
            <Field label="Veículo" htmlFor="romaneio-veiculo"><Select id="romaneio-veiculo" value={form.veiculo_id || ''} onChange={(e) => setForm({ ...form, veiculo_id: e.target.value })} disabled={visualizando}><option value="">Selecione</option>{veiculosValidos.map((item) => <option key={item.id} value={getUuidCandidate(item) || ''}>{item.placa} - {item.modelo || item.marca || 'Veículo'}</option>)}</Select></Field>
            <Field label="Observações" htmlFor="romaneio-observacoes" className="nx-sngpc-field--full"><Textarea id="romaneio-observacoes" value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} disabled={visualizando} /></Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Itens do romaneio</h2>
            {!visualizando ? <Button type="button" variant="ghost" onClick={abrirProdutos}><Plus size={15} /> Adicionar produtos</Button> : null}
          </div>
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Lote</th>
                  <th>Quantidade</th>
                  {!visualizando ? <th className="nx-col-acoes">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {form.itens.length === 0 ? (
                  <tr><td colSpan={visualizando ? 3 : 4} className="nx-table-loading">Nenhum item informado.</td></tr>
                ) : form.itens.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>
                      {visualizando ? (
                        item.produto_descricao || item.produto_codigo || item.produto_id
                      ) : (
                        <div>
                          <strong>{produtoPorId(item.produto_id)?.codigo || '-'}</strong>
                          <span className="nx-rastreabilidade-muted">{produtoPorId(item.produto_id)?.descricao || 'Produto não localizado'}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {visualizando ? (
                        item.lote_numero || '-'
                      ) : (
                        <Select value={item.lote_id || ''} onChange={(e) => atualizarItem(index, { lote_id: e.target.value || null })}>
                          <option value="">Sem lote</option>
                          {(lotesPorProduto[index] || []).filter((lote) => getUuidCandidate(lote)).map((lote) => <option key={lote.id} value={getUuidCandidate(lote) || ''}>{lote.numero_lote}</option>)}
                        </Select>
                      )}
                    </td>
                    <td>
                      {visualizando ? item.quantidade : <DecimalInput value={item.quantidade} onValueChange={(value) => atualizarItem(index, { quantidade: value ?? 0 })} />}
                    </td>
                    {!visualizando ? (
                      <td className="nx-col-acoes">
                        <IconButton label="Remover" variant="danger" onClick={() => removerItem(index)}><Trash2 size={15} aria-hidden /></IconButton>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </Card>
      <FormStickyFooter cancelLabel={visualizando ? 'Voltar' : 'Cancelar'} onCancel={() => navigate('/producao/romaneios')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} disabled={visualizando} />

      <Dialog open={modalProdutosAberto} onOpenChange={setModalProdutosAberto}>
        <DialogContent className="nx-component-picker-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar produtos</DialogTitle>
            <DialogDescription>Selecione um ou mais produtos para inserir na grade de itens do romaneio.</DialogDescription>
          </DialogHeader>

          <div className="nx-component-picker">
            <div className="nx-search-field nx-search-field--filter nx-component-picker__search">
              <Input
                type="search"
                value={buscaProduto}
                onChange={(event) => setBuscaProduto(event.target.value)}
                placeholder="Buscar por código ou descrição..."
                aria-label="Buscar produtos"
              />
            </div>

            <div className="nx-component-picker__meta">
              <span>{produtosDisponiveis.length} produtos encontrados</span>
              <span>{produtosSelecionados.length} selecionados</span>
            </div>

            <div className="nx-component-picker__list">
              {produtosDisponiveis.length === 0 ? (
                <div className="nx-component-picker__empty">Nenhum produto encontrado.</div>
              ) : (
                produtosDisponiveis.map((produto) => {
                  const uuid = getUuidCandidate(produto) || '';
                  const checked = produtosSelecionados.includes(uuid);

                  return (
                    <label key={produto.id} className="nx-component-picker__row">
                      <Checkbox checked={checked} onChange={() => toggleProdutoSelecionado(uuid)} />
                      <span className="nx-component-picker__product">
                        <strong>{produto.codigo || '-'}</strong>
                        <span>{produto.descricao}</span>
                      </span>
                      <span className="nx-component-picker__unit">{produto.unidade || '-'}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setModalProdutosAberto(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmarProdutos} disabled={produtosSelecionados.length === 0}>
              <Plus size={16} />
              Adicionar selecionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}
